import { useEffect, useState, useRef } from 'react';

// Hook that reports visibility of an element and the current page scroll direction.
export default function useScrollFadeEffect(ref) {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('down');
  const lastY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const rafRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Set visibility based on whether the element intersects the viewport.
        // Additionally, capture instantaneous scroll direction at intersection time
        // by comparing current scroll Y to the last stored value. This helps
        // AnimatedWrapper decide whether the element should enter from above
        // or below when the user is scrolling up vs down.
        const isIntersecting = !!entry.isIntersecting;
        setIsVisible(isIntersecting);

        try {
          const y = window.scrollY || window.pageYOffset || 0;
          if (y < lastY.current) {
            setScrollDirection('up');
          } else if (y > lastY.current) {
            setScrollDirection('down');
          }
          lastY.current = y;
        } catch (e) {
          // defensive: window may be undefined in some SSR contexts
        }
      },
      { threshold: 0.1 } // Trigger when 10% of the element is visible
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ref]);

  useEffect(() => {
    // track scroll direction; use rAF to throttle updates
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset;
        if (y < lastY.current) {
          setScrollDirection('up');
        } else if (y > lastY.current) {
          setScrollDirection('down');
        }
        lastY.current = y;
        rafRef.current = null;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchmove', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchmove', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return { isVisible, scrollDirection };
}